// Which clips that CLAIM to be the same material actually are.
//
// The shelf and the pad grid group by description (lib/material.ts), because
// the audio object over-splits: Live's clip-edge fades give one loop several
// content hashes. But the description under-splits — a transposed duplicate or
// a re-bounced MIDI lane can match on every field and still be a different
// sound — and a wrong merge takes one of them off the shelf entirely.
//
// So this asks the audio, but only where there is a question to ask: a group
// of clips that describe themselves identically while pointing at DIFFERENT
// objects. Everything else costs nothing. On a Set with no such collision this
// hook makes no requests at all; on the one that started this it makes four.
//
// The objects in question are resolved TOGETHER, and that is the point rather
// than a convenience: "is this the same piece as that" is the only question
// there is, and an object has no answer to it on its own (material-audio.ts
// has the measurement — renders of one loop sit at different offsets, so no
// fixed-offset hash of one object can agree with another's). Holding the whole
// colliding set at once is what lets them be compared instead of hashed.
//
// Optimistic: until the windows land the description stands alone, so the
// common case (one loop, several renders) is right immediately and the rare
// one corrects itself a moment later. The other order would show every Set its
// duplicates first and tidy them away after, which is the wrong bet.

import { useEffect, useMemo, useRef, useState } from "react";
import { audioFetch } from "@web-shared/lib/api";
import { materialKey, roomClipFacts } from "@web-shared/lib/material";
import { audioWindow, clusterVariants, type AudioWindow } from "@/lib/material-audio";
import type { CanonicalRoom } from "@shared/sync/state.js";

/** Reads a resolved variant, or undefined while none is needed or known. */
export type VariantOf = (audioKey: string) => string | undefined;

/**
 * The objects worth fetching a window for: those sharing a track and a
 * description with another object that is not them.
 */
export function collidingObjects(room: CanonicalRoom | null): string[] {
  if (!room) return [];
  const byDescription = new Map<string, Set<string>>();
  for (const clip of room.clips) {
    if (clip.audioKey === null) continue;
    const key = `${clip.trackId}|${materialKey(roomClipFacts(clip))}`;
    const keys = byDescription.get(key) ?? new Set<string>();
    keys.add(clip.audioKey);
    byDescription.set(key, keys);
  }
  const out = new Set<string>();
  for (const keys of byDescription.values()) {
    if (keys.size > 1) for (const k of keys) out.add(k);
  }
  return [...out].sort();
}

/** The probe's ranged reads: the URL and credentials the player downloads with,
 * so they land on the same cache entry and not one that rotates with the token. */
const probeFetch: typeof fetch = (url, init) => audioFetch(String(url), init);

export function useMaterialVariants(
  room: CanonicalRoom | null,
  urlOf: (audioKey: string) => string | undefined,
  /** Resolves when the player has stopped downloading: see `fetchesSettled`. */
  settled: () => Promise<void> = () => Promise.resolve(),
): VariantOf {
  const [variants, setVariants] = useState<Record<string, string>>({});

  // A stable identity for "which objects are in question", so editing a clip's
  // volume does not re-run the probe.
  const colliding = useMemo(() => collidingObjects(room), [room]);
  const collidingId = colliding.join(",");
  // Read when the probe runs, not effect dependencies: the page re-renders every
  // frame during playback with new function identities, and the effect waits for
  // the downloads to settle, so restarting it each frame meant it never got there.
  const urlOfNow = useRef(urlOf);
  urlOfNow.current = urlOf;
  const settledNow = useRef(settled);
  settledNow.current = settled;

  useEffect(() => {
    if (colliding.length === 0) return;
    let live = true;
    void settledNow.current().then(() => !live ? [] : Promise.all(
      colliding.map(async (audioKey) => {
        const url = urlOfNow.current(audioKey);
        if (!url) return null;
        const window = await audioWindow(audioKey, url, probeFetch);
        // Null is "could not tell" — leave the description standing rather
        // than inventing a difference (lib/material-audio.ts).
        return window === null ? null : ([audioKey, window] as const);
      }),
    )).then((pairs) => {
      if (!live) return;
      const landed = pairs.filter((p): p is readonly [string, AudioWindow] => p !== null);
      if (landed.length === 0) return;
      const ids = clusterVariants(new Map(landed));
      setVariants((prev) => {
        // Only a change is worth a render — this runs on every room edit.
        let same = true;
        for (const [k, v] of ids) if (prev[k] !== v) same = false;
        return same ? prev : { ...prev, ...Object.fromEntries(ids) };
      });
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collidingId]);

  return useMemo(() => (audioKey: string) => variants[audioKey], [variants]);
}
